import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEnrollmentProgress } from '../hooks/useCompetency';

const CompetencyCoursePlayer = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const { progress, loading, error, markModuleComplete } = useEnrollmentProgress(parseInt(enrollmentId));

  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [completingModule, setCompletingModule] = useState(false);

  useEffect(() => {
    // Auto-advance to first incomplete module
    if (progress && progress.enrollment?.course?.modules) {
      const completedIds = progress.completed_modules || [];
      const modules = progress.enrollment.course.modules;

      const firstIncomplete = modules.findIndex(
        module => !completedIds.includes(module.id)
      );

      if (firstIncomplete !== -1) {
        setCurrentModuleIndex(firstIncomplete);
      }
    }
  }, [progress]);

  const handleMarkComplete = async () => {
    if (!currentModule) return;

    setCompletingModule(true);
    try {
      await markModuleComplete(currentModule.id);

      // Move to next module if available
      if (currentModuleIndex < modules.length - 1) {
        setCurrentModuleIndex(currentModuleIndex + 1);
      }
    } catch (err) {
      alert('Failed to mark module as complete: ' + err.message);
    } finally {
      setCompletingModule(false);
    }
  };

  const handlePrevious = () => {
    if (currentModuleIndex > 0) {
      setCurrentModuleIndex(currentModuleIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentModuleIndex < modules.length - 1) {
      setCurrentModuleIndex(currentModuleIndex + 1);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <p>Loading course...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
          <strong>Error:</strong> {error}
        </div>
        <Link to="/competency/my-courses" style={{ display: 'inline-block', marginTop: '20px' }}>
          ← Back to My Courses
        </Link>
      </div>
    );
  }

  if (!progress || !progress.enrollment) {
    return (
      <div className="container">
        <p>Enrollment not found</p>
        <Link to="/competency/my-courses">← Back to My Courses</Link>
      </div>
    );
  }

  const enrollment = progress.enrollment;
  const course = enrollment.course;
  const modules = course.modules || [];
  const currentModule = modules[currentModuleIndex];
  const completedModuleIds = progress.completed_modules || [];
  const isCurrentModuleComplete = currentModule && completedModuleIds.includes(currentModule.id);
  const allModulesComplete = modules.every(m => completedModuleIds.includes(m.id));

  return (
    <div className="container">
      <Link to="/competency/my-courses" style={{ display: 'inline-block', marginBottom: '20px', color: '#D4AF37' }}>
        ← Back to My Courses
      </Link>

      {/* Course Header */}
      <div style={{
        background: 'linear-gradient(135deg, #D4AF37, #B8860B)',
        color: '#fff',
        padding: '30px',
        borderRadius: '8px',
        marginBottom: '30px'
      }}>
        <h1 style={{ margin: '0 0 10px 0' }}>{course.title}</h1>
        <div style={{ fontSize: '16px', opacity: 0.95 }}>
          <strong>Progress:</strong> {completedModuleIds.length} / {modules.length} modules completed
          ({Math.round(progress.progress_percentage || 0)}%)
        </div>
      </div>

      {allModulesComplete && (
        <div style={{
          padding: '20px',
          background: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0, color: '#155724' }}>
            Congratulations! You've completed all modules!
          </h3>
          <p style={{ marginBottom: '15px', color: '#155724' }}>
            You can now proceed to the practical assessment.
          </p>
          <Link
            to={`/competency/assessment/${enrollmentId}`}
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: '#28a745',
              color: '#fff',
              borderRadius: '4px',
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            Start Assessment
          </Link>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '30px' }}>
        {/* Module List Sidebar */}
        <div>
          <h3 style={{ marginTop: 0 }}>Course Modules</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {modules.map((module, idx) => {
              const isComplete = completedModuleIds.includes(module.id);
              const isCurrent = idx === currentModuleIndex;

              return (
                <button
                  key={module.id}
                  onClick={() => setCurrentModuleIndex(idx)}
                  style={{
                    padding: '15px',
                    textAlign: 'left',
                    border: isCurrent ? '2px solid #D4AF37' : '1px solid #ddd',
                    borderRadius: '8px',
                    background: isCurrent ? '#fff9e6' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      background: isComplete ? '#28a745' : '#ddd',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      {isComplete ? ''  : idx + 1}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: isCurrent ? 'bold' : 'normal' }}>
                      {module.title || `Module ${idx + 1}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Module Content */}
        {currentModule ? (
          <div>
            <div style={{
              border: '2px solid #D4AF37',
              borderRadius: '8px',
              padding: '30px',
              background: '#fff',
              minHeight: '500px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: isCurrentModuleComplete ? '#28a745' : '#D4AF37',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {isCurrentModuleComplete ? ''  : currentModuleIndex + 1}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '24px' }}>
                    {currentModule.title || `Module ${currentModuleIndex + 1}`}
                  </h2>
                  {isCurrentModuleComplete && (
                    <span style={{
                      display: 'inline-block',
                      marginTop: '5px',
                      padding: '4px 12px',
                      background: '#d4edda',
                      color: '#155724',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      Completed
                    </span>
                  )}
                </div>
              </div>

              {/* Module Description */}
              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '4px',
                marginBottom: '30px',
                lineHeight: '1.8',
                fontSize: '16px'
              }}>
                {currentModule.description || 'Module content will be displayed here.'}
              </div>

              {/* Module Content/Materials */}
              {currentModule.content && (
                <div style={{ marginBottom: '30px' }}>
                  <h3>Learning Materials</h3>
                  <div style={{
                    padding: '20px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    lineHeight: '1.8'
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: currentModule.content }} />
                  </div>
                </div>
              )}

              {/* Learning Objectives */}
              {currentModule.learning_objectives && currentModule.learning_objectives.length > 0 && (
                <div style={{ marginBottom: '30px' }}>
                  <h3>Learning Objectives</h3>
                  <ul style={{ lineHeight: '2' }}>
                    {currentModule.learning_objectives.map((obj, idx) => (
                      <li key={idx}>{obj}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Resources */}
              {currentModule.resources && currentModule.resources.length > 0 && (
                <div style={{ marginBottom: '30px' }}>
                  <h3>Additional Resources</h3>
                  <ul>
                    {currentModule.resources.map((resource, idx) => (
                      <li key={idx}>
                        <a href={resource.url} target="_blank" rel="noopener noreferrer" style={{ color: '#D4AF37' }}>
                          {resource.title}
                        </a>
                        {resource.description && <span> - {resource.description}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Mark as Complete Button */}
              {!isCurrentModuleComplete && (
                <div style={{
                  marginTop: '40px',
                  padding: '20px',
                  background: '#e3f2fd',
                  border: '1px solid #2196f3',
                  borderRadius: '4px',
                  textAlign: 'center'
                }}>
                  <p style={{ marginBottom: '15px', fontSize: '16px' }}>
                    Have you completed this module?
                  </p>
                  <button
                    onClick={handleMarkComplete}
                    disabled={completingModule}
                    style={{
                      padding: '12px 30px',
                      background: '#2196f3',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: completingModule ? 'not-allowed' : 'pointer',
                      opacity: completingModule ? 0.6 : 1
                    }}
                  >
                    {completingModule ? 'Marking Complete...' : 'Mark as Complete'}
                  </button>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '20px'
            }}>
              <button
                onClick={handlePrevious}
                disabled={currentModuleIndex === 0}
                style={{
                  padding: '12px 24px',
                  background: currentModuleIndex === 0 ? '#ddd' : '#fff',
                  color: currentModuleIndex === 0 ? '#999' : '#333',
                  border: '2px solid #ddd',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: currentModuleIndex === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous Module
              </button>

              <button
                onClick={handleNext}
                disabled={currentModuleIndex === modules.length - 1}
                style={{
                  padding: '12px 24px',
                  background: currentModuleIndex === modules.length - 1 ? '#ddd' : '#D4AF37',
                  color: currentModuleIndex === modules.length - 1 ? '#999' : '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: currentModuleIndex === modules.length - 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Next Module →
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', background: '#f5f5f5', borderRadius: '4px' }}>
            <p>No modules available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetencyCoursePlayer;
